import { useEffect } from 'react';

/**
 * Custom hook for managing SEO meta tags per page
 * Updates document title, description, and Open Graph tags
 */
function useSEO({ 
  title, 
  description, 
  image,
  url,
  type = 'article',
  noIndex = false
}) {
  useEffect(() => {
    // Update document title
    const previousTitle = document.title;
    document.title = title ? `${title} | Veydra` : 'Veydra';

    // Helper to update or create meta tag
    const setMetaTag = (property, content, isOG = false) => {
      if (!content) return;
      const attr = isOG ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard meta tags
    setMetaTag('description', description);

    // Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', type, true);
    if (image) {
      // Ensure absolute URL for images
      const absoluteImage = image.startsWith('http') 
        ? image 
        : `${window.location.origin}${image}`;
      setMetaTag('og:image', absoluteImage, true);
    }
    if (url) {
      setMetaTag('og:url', url, true);
    }

    // Twitter Card tags
    setMetaTag('twitter:card', image ? 'summary_large_image' : 'summary');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    if (image) {
      const absoluteImage = image.startsWith('http') 
        ? image 
        : `${window.location.origin}${image}`;
      setMetaTag('twitter:image', absoluteImage);
    }

    // Indexing control
    if (noIndex) {
      setMetaTag('robots', 'noindex, nofollow');
      setMetaTag('googlebot', 'noindex, nofollow');
    }

    // Cleanup: restore previous title on unmount
    return () => {
      document.title = previousTitle;
    };
  }, [title, description, image, url, type, noIndex]);
}

export default useSEO;
